import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  ProgressBar,
  Alert,
  Spinner,
  ListGroup,
} from "react-bootstrap";
import {
  FaCrown,
  FaRocket,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaChartLine,
  FaInfoCircle,
  FaTimes,
  FaCalculator,
  FaBan,
} from "react-icons/fa";
import {
  getCurrentUserPlan,
  cancelPlan,
  clearError,
} from "../../redux/slices/paymentSlice";
import { toast } from "react-toastify";
import { plans } from "../../utils/static";
import PlanApiAccessList from "../shared/PlanApiAccessList";
import CancelSubscriptionModal from "./subscription/CancelSubscriptionModal";
import { formatPlanApiCallsLabel } from "../../utils/planApiAccess";
import { formatPlanDisplayPrice } from "../../utils/planPricing";
import { post } from "../../utils/makeRequest";
import { openCashfreeSubscriptionCheckout } from "../../utils/cashfreeCheckout";

const MyPlan = () => {
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const dispatch = useDispatch();
  const { currentPlan, planLoading, planError } = useSelector(
    (state) => state.payment
  );

  useEffect(() => {
    dispatch(getCurrentUserPlan());
  }, [dispatch]);

  useEffect(() => {
    let intervalId = null;
    let toastShown = false;
    try {
      const raw = localStorage.getItem("pending_plan_activation");
      if (!raw) return;
      const data = JSON.parse(raw);
      const ts = Number(data?.ts || 0);
      const ageMs = Date.now() - ts;

      if (!ts || ageMs > 10 * 60 * 1000) {
        localStorage.removeItem("pending_plan_activation");
        return;
      }

      // Show a generic verification UI when a payment flow was initiated.
      // If we can detect a failed/cancelled redirect, clear the flag and show an error.
      try {
        const params = new URLSearchParams(window.location.search || "");
        const cfStatusRaw =
          params.get("cf_status") ||
          params.get("status") ||
          params.get("txStatus") ||
          params.get("payment_status") ||
          "";
        const cfStatus = String(cfStatusRaw).toUpperCase().trim();
        const failureStatuses = new Set(["FAILED", "CANCELLED", "CANCELED", "FAILURE"]);

        if (failureStatuses.has(cfStatus)) {
          localStorage.removeItem("pending_plan_activation");
          toast.error("Payment was not completed. Please try again.");
          return;
        }
      } catch (_) {}

      if (!toastShown) {
        toastShown = true;
        toast.info(
          "We’re verifying your payment with Cashfree. This may take some time. If your payment is successful, your plan will activate shortly."
        );
      }

      intervalId = setInterval(() => {
        dispatch(getCurrentUserPlan());
      }, 10_000);

      return () => {
        if (intervalId) clearInterval(intervalId);
      };
    } catch (_) {
      try {
        localStorage.removeItem("pending_plan_activation");
      } catch (_) {}
      if (intervalId) clearInterval(intervalId);
    }
  }, [dispatch]);

  // Upfront payment flow: verify order → activate → start mandate checkout for renewals.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = localStorage.getItem("pending_upfront_activation");
        if (!raw) return;
        const data = JSON.parse(raw);
        const ts = Number(data?.ts || 0);
        const orderId = data?.orderId;
        const ageMs = Date.now() - ts;

        if (!ts || ageMs > 15 * 60 * 1000 || !orderId) {
          localStorage.removeItem("pending_upfront_activation");
          return;
        }

        toast.info("Verifying your payment and activating your plan...");

        const resp = await post("/payment/verify-upfront-payment", {
          order_id: orderId,
        });

        if (cancelled) return;

        if (resp?.success) {
          toast.success("Payment verified. Plan activated.");
          dispatch(getCurrentUserPlan());

          const mandate = resp?.mandate;
          if (mandate?.subscriptionSessionId) {
            toast.info("Please authorize AutoPay to enable renewals.");
            // Track mandate authorization like existing pending flow (for UI polling)
            try {
              localStorage.setItem(
                "pending_plan_activation",
                JSON.stringify({
                  ts: Date.now(),
                  targetPlan: String(resp?.plan || "").toUpperCase().trim() || null,
                })
              );
            } catch (_) {}

            await openCashfreeSubscriptionCheckout({
              subscriptionSessionId: mandate.subscriptionSessionId,
              cashfreeEnv: mandate.cashfreeEnv,
            });
          }

          localStorage.removeItem("pending_upfront_activation");
        } else {
          toast.error(resp?.error || "Failed to verify payment");
        }
      } catch (e) {
        const data = e?.response?.data;
        const msg =
          data?.details || data?.error || e?.message || "Failed to verify payment";
        toast.error(msg);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem("pending_plan_activation");
      if (!raw || !currentPlan) return;
      const data = JSON.parse(raw);
      const targetPlan = String(data?.targetPlan || "").toUpperCase().trim();
      const current = String(currentPlan?.current_plan || "").toUpperCase().trim();
      if (targetPlan && current === targetPlan) {
        localStorage.removeItem("pending_plan_activation");
        toast.success("Your plan is now active.");
      }
    } catch (_) {}
  }, [currentPlan]);

  useEffect(() => {
    if (planError) {
      toast.error(planError);
      dispatch(clearError());
    }
  }, [planError, dispatch]);

  const handleCancelPlan = async () => {
    setCancelLoading(true);
    try {
      const result = await dispatch(
        cancelPlan("User requested cancellation")
      ).unwrap();
      toast.success(
        result.message || "Your subscription has been cancelled immediately."
      );
      setShowCancelModal(false);
      dispatch(getCurrentUserPlan());
    } catch (error) {
      console.error("Cancel plan error:", error);
      const message =
        error?.error || error?.message || "Failed to cancel plan. Please try again.";
      toast.error(message);
    } finally {
      setCancelLoading(false);
    }
  };

  const getPlanIcon = (planName) => {
    if (planName === "none") return FaBan;
    const plan = plans.find((p) => p.backendName === planName?.toUpperCase());
    return plan?.icon || FaRocket;
  };

  const getPlanColor = (planName) => {
    if (planName === "none") return "secondary";
    const plan = plans.find((p) => p.backendName === planName?.toUpperCase());
    return plan?.color || "primary";
  };

  const getPlanGradient = (planName) => {
    if (planName === "none")
      return "linear-gradient(135deg, #6c757d 0%, #5a6268 100%)";
    const plan = plans.find((p) => p.backendName === planName?.toUpperCase());
    return (
      plan?.gradient || "linear-gradient(135deg, #007bff 0%, #0056b3 100%)"
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatCurrency = (amount, currency = "INR") => {
    if (!amount && amount !== 0) return "Free";

    const cur = String(currency).toUpperCase();
    const symbol = cur === "USD" ? "$" : "₹";
    return `${symbol}${formatPlanDisplayPrice(amount, cur)}`;
  };

  const calculateUsagePercentage = () => {
    if (currentPlan?.is_unlimited) return 0;
    if (!currentPlan?.monthly_api_limit || !currentPlan?.api_limit_remaining)
      return 0;

    const used =
      currentPlan.monthly_api_limit - currentPlan.api_limit_remaining;
    return Math.round((used / currentPlan.monthly_api_limit) * 100);
  };

  // Add helper function to check if subscription is scheduled for cancellation
  const isScheduledForCancellation = () => {
    return currentPlan?.cancellation_status === "scheduled_for_cancellation";
  };

  if (planLoading) {
    return (
      <Container fluid className="p-4">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading your plan details...</p>
        </div>
      </Container>
    );
  }

  if (!currentPlan) {
    return (
      <Container fluid className="p-4">
        <Alert variant="warning">
          <FaExclamationTriangle className="me-2" />
          No plan information available. Please contact support.
        </Alert>
      </Container>
    );
  }

  const IconComponent = getPlanIcon(currentPlan.current_plan);
  const usagePercentage = calculateUsagePercentage();
  const isNoPlan = currentPlan.current_plan === "none";
  // AutoPay is currently hidden from this screen.

  return (
    <Container fluid className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold text-primary">My Plan</h2>
        {!isNoPlan && currentPlan.current_plan !== "FREE" && (
          <div className="d-flex gap-2">
            <Button
              variant="outline-danger"
              onClick={() => setShowCancelModal(true)}
              disabled={currentPlan.subscription_status !== "active"}
            >
              <FaTimes className="me-2" />
              Cancel Subscription
            </Button>
          </div>
        )}
      </div>

      {/* No Plan Alert */}
      {isNoPlan && (
        <Alert variant="warning" className="mb-4">
          <FaBan className="me-2" />
          <strong>No Active Subscription</strong>
          <br />
          You don't have an active subscription plan. Choose a plan to start
          using our API services.
          <div className="mt-2">
            <Button variant="primary" href="/dashboard/subscription">
              Choose a Plan
            </Button>
          </div>
        </Alert>
      )}

      <Row className="g-4">
        {/* Current Plan Card */}
        <Col lg={8}>
          <Card className="h-100 border-0 shadow">
            <Card.Header
              className="text-white border-0"
              style={{
                background: isScheduledForCancellation()
                  ? "linear-gradient(135deg, #dc3545 0%, #c82333 100%)"
                  : getPlanGradient(currentPlan.current_plan),
                minHeight: "120px",
              }}
            >
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="d-flex align-items-center mb-2">
                    <IconComponent size={32} className="me-3" />
                    <div>
                      <h4 className="mb-1">
                        {isNoPlan
                          ? "No Plan"
                          : `${currentPlan.current_plan} Plan`}
                      </h4>
                      <p className="mb-0 opacity-90">
                        {isNoPlan
                          ? "No active subscription"
                          : isScheduledForCancellation()
                          ? "Scheduled for cancellation"
                          : currentPlan.is_custom_plan
                          ? "Custom API Plan"
                          : "Standard Plan"}
                      </p>
                    </div>
                  </div>

                  <div className="d-flex gap-2">
                    <Badge
                      bg={
                        isNoPlan
                          ? "secondary"
                          : isScheduledForCancellation()
                          ? "danger"
                          : "light"
                      }
                      text="dark"
                    >
                      {isNoPlan
                        ? "No Subscription"
                        : isScheduledForCancellation()
                        ? "Cancelling Soon"
                        : currentPlan.subscription_status === "active"
                        ? "Active"
                        : "Inactive"}
                    </Badge>
                    {currentPlan.is_unlimited && (
                      <Badge bg="warning">
                        <FaCrown className="me-1" />
                        Unlimited
                      </Badge>
                    )}
                    {currentPlan.is_custom_plan && (
                      <Badge bg="info">
                        <FaCalculator className="me-1" />
                        Custom
                      </Badge>
                    )}
                  </div>
                </div>

                {!isNoPlan && currentPlan.subscription_amount && !currentPlan.is_custom_plan && (
                  <div className="text-end">
                    <h3 className="mb-0">
                      {formatCurrency(
                        currentPlan.subscription_amount,
                        currentPlan.subscription_currency
                      )}
                    </h3>
                    <small className="opacity-75">
                      /{currentPlan.billing_interval || "month"}
                    </small>
                  </div>
                )}
              </div>
            </Card.Header>

            <Card.Body>
              {/* API Usage */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">
                    <FaChartLine className="me-2" />
                    API Usage This Month
                  </h6>
                  {!isNoPlan &&
                    (!currentPlan.is_unlimited || currentPlan.is_custom_plan) && (
                    <span className="text-muted small">
                      {currentPlan.api_limit_remaining?.toLocaleString()} /{" "}
                      {currentPlan.monthly_api_limit?.toLocaleString()}{" "}
                      remaining
                    </span>
                  )}
                </div>

                {isNoPlan ? (
                  <Alert variant="secondary" className="mb-0">
                    <FaBan className="me-2" />
                    No API access - Please choose a plan to start using our
                    services
                  </Alert>
                ) : currentPlan.is_unlimited && !currentPlan.is_custom_plan ? (
                  <Alert variant="success" className="mb-0">
                    <FaCrown className="me-2" />
                    Unlimited API calls - no usage limits!
                  </Alert>
                ) : currentPlan.is_custom_plan ? (
                  <Alert variant="info" className="mb-0">
                    <FaCalculator className="me-2" />
                    Custom plan limits apply for your billing period.
                  </Alert>
                ) : (
                  <>
                    <ProgressBar
                      now={usagePercentage}
                      variant={
                        usagePercentage > 80
                          ? "danger"
                          : usagePercentage > 60
                          ? "warning"
                          : "success"
                      }
                      className="mb-2"
                      style={{ height: "10px" }}
                    />
                    <div className="d-flex justify-content-between small text-muted">
                      <span>{usagePercentage}% used</span>
                      <span>{100 - usagePercentage}% remaining</span>
                    </div>
                  </>
                )}
              </div>

              {/* Plan API access */}
              <div className="mb-4">
                {isNoPlan ? (
                  <p className="text-muted small mb-0">
                    No API access without an active subscription.
                  </p>
                ) : (
                  <PlanApiAccessList
                    planBackendName={currentPlan.current_plan}
                    apiCallsLabel={formatPlanApiCallsLabel(
                      currentPlan.current_plan,
                      currentPlan.monthly_api_limit
                    )}
                    title="API Access"
                    titleClassName="h6 mb-3"
                    listClassName="list mb-0"
                  />
                )}
              </div>

              {/* Billing Information */}
              {!isNoPlan && currentPlan.subscription_status === "active" && (
                <div className="border-top pt-3">
                  <h6 className="mb-3">
                    <FaCalendarAlt className="me-2" />
                    Billing Information
                  </h6>
                  <Row className="g-3">
                    {isScheduledForCancellation() ? (
                      <>
                        <Col sm={6}>
                          <div className="text-muted small">
                            Cancellation Date
                          </div>
                          <div className="fw-bold text-danger">
                            {formatDate(
                              currentPlan.scheduled_cancellation_date
                            )}
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="text-muted small">Status</div>
                          <div className="fw-bold text-warning">
                            Scheduled for Cancellation
                          </div>
                        </Col>
                      </>
                    ) : (
                      <>
                        <Col sm={6}>
                          <div className="text-muted small">
                            Next Billing Date
                          </div>
                          <div className="fw-bold">
                            {formatDate(currentPlan.next_billing_date)}
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="text-muted small">
                            Subscription Started
                          </div>
                          <div className="fw-bold">
                            {formatDate(currentPlan.subscription_start_date)}
                          </div>
                        </Col>
                      </>
                    )}
                  </Row>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Side Panel */}
        <Col lg={4}>
          <div className="d-flex flex-column gap-3">
            {/* Quick Stats */}
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-light border-0">
                <h6 className="mb-0">Quick Stats</h6>
              </Card.Header>
              <Card.Body>
                <ListGroup variant="flush">
                  <ListGroup.Item className="d-flex justify-content-between px-0">
                    <span>Plan Level</span>
                    <Badge bg={getPlanColor(currentPlan.current_plan)}>
                      {isNoPlan ? "No Plan" : currentPlan.current_plan}
                    </Badge>
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex justify-content-between px-0">
                    <span>Status</span>
                    <Badge
                      bg={
                        isNoPlan
                          ? "secondary"
                          : currentPlan.subscription_status === "active"
                          ? "success"
                          : "secondary"
                      }
                    >
                      {isNoPlan
                        ? "No Subscription"
                        : currentPlan.subscription_status || "Inactive"}
                    </Badge>
                  </ListGroup.Item>
                  <ListGroup.Item className="d-flex justify-content-between px-0">
                    <span>API Type</span>
                    <span className="fw-bold">
                      {isNoPlan
                        ? "No Access"
                        : currentPlan.is_unlimited
                        ? "Unlimited"
                        : "Limited"}
                    </span>
                  </ListGroup.Item>
                </ListGroup>
              </Card.Body>
            </Card>

            {/* Upgrade/Subscribe Notice */}
            {(isNoPlan || currentPlan.current_plan !== "ULTIMATE") && (
              <Alert variant={isNoPlan ? "warning" : "info"}>
                <FaInfoCircle className="me-2" />
                <strong>
                  {isNoPlan ? "Get Started Today!" : "Want more features?"}
                </strong>
                <br />
                <small>
                  {isNoPlan
                    ? "Choose a subscription plan to start using our powerful API services."
                    : currentPlan.upgrade_message}
                </small>
                <div className="mt-2">
                  <Button
                    variant="primary"
                    size="sm"
                    href="/dashboard/subscription"
                  >
                    {isNoPlan ? "Choose Plan" : "Upgrade Plan"}
                  </Button>
                </div>
              </Alert>
            )}

            {/* Support */}
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center">
                <h6>Need Help?</h6>
                <p className="small text-muted mb-3">
                  Contact our support team for any questions about your plan.
                </p>
                <Button
                  variant="outline-primary"
                  size="sm"
                  href="https://wa.me/919817800346"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Contact Support
                </Button>
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>

      {/* Cancel Plan Modal */}
      <CancelSubscriptionModal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        onConfirm={handleCancelPlan}
        loading={cancelLoading}
      />
    </Container>
  );
};

export default MyPlan;
